import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'

import { can } from '@/features/auth/permissions'
import { useSession } from '@/features/auth/use-session'
import { CompanyMonogram, CompanySpine, PageHeader, UserAvatar } from '@/shared/components/common'
import { EmptyState, ErrorState, NoResultsState, SkeletonRows } from '@/shared/components/data'
import { Button, Input } from '@/shared/components/ui'
import { useT } from '@/shared/i18n'
import { cn } from '@/shared/lib/cn'

import { useCompanies } from './api'
import { CompanyForm } from './components/CompanyForm'

const HEAD = 'px-2 py-1.5 text-2xs font-normal tracking-wide text-text-3 uppercase'

/** `/companies` — listado denso. Ordenado por vencidas: primero lo que duele. */
export function CompaniesPage() {
  const t = useT()
  const [search, setSearch] = useState('')
  const [creating, setCreating] = useState(false)
  const query = useCompanies()
  const { me } = useSession()
  // `company:create` ya existía en la matriz de permisos y no lo consultaba
  // nadie: el permiso estaba escrito y el botón no existía.
  const puedeCrear = can(me, 'company:create')

  const companies = useMemo(() => {
    const needle = search.trim().toLowerCase()
    if (!needle) return query.data ?? []
    return (query.data ?? []).filter((company) =>
      `${company.name} ${company.slug}`.toLowerCase().includes(needle),
    )
  }, [query.data, search])

  return (
    <div className="flex flex-col">
      <PageHeader
        title={t('common.label.companies')}
        description={t('companies.list.description')}
        actions={
          puedeCrear ? (
            <Button variant="primary" onClick={() => setCreating(true)}>
              {t('companies.new')}
            </Button>
          ) : null
        }
      />

      <CompanyForm open={creating} onClose={() => setCreating(false)} />

      <div className="flex flex-col gap-3 p-4">
        <Input
          type="search"
          aria-label={t('companies.list.searchLabel')}
          placeholder={t('companies.list.searchPlaceholder')}
          className="max-w-[280px]"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />

        {query.isPending ? (
          <SkeletonRows rows={5} columns={[0.3, 0.2, 0.12, 0.12, 0.12]} />
        ) : query.isError ? (
          <ErrorState error={query.error} onRetry={() => void query.refetch()} />
        ) : (query.data ?? []).length === 0 ? (
          <EmptyState
            title={t('companies.empty.title')}
            description={t('companies.empty.description')}
          />
        ) : companies.length === 0 ? (
          <NoResultsState onClearFilters={() => setSearch('')} />
        ) : (
          <div className="hairline overflow-x-auto rounded-[var(--radius-card)] bg-surface-1">
            <table className="w-full min-w-[720px]">
              <thead>
                <tr className="hairline-b">
                  <th scope="col" className={cn(HEAD, 'text-left')}>
                    {t('common.label.company')}
                  </th>
                  <th scope="col" className={cn(HEAD, 'text-left')}>
                    {t('common.label.owner')}
                  </th>
                  <th scope="col" className={cn(HEAD, 'text-right')}>
                    {t('companies.column.activeProjects')}
                  </th>
                  <th scope="col" className={cn(HEAD, 'text-right')}>
                    {t('companies.column.openTasks')}
                  </th>
                  <th scope="col" className={cn(HEAD, 'text-right')}>
                    {t('companies.column.overdueTasks')}
                  </th>
                  <th scope="col" className={cn(HEAD, 'text-right')}>
                    {t('companies.column.contacts')}
                  </th>
                </tr>
              </thead>

              <tbody>
                {companies.map((company) => (
                  <tr
                    key={company.id}
                    className="hairline-b hover:bg-surface-2/60"
                    style={{ height: 'var(--row-h)' }}
                  >
                    <td className="px-2">
                      <Link
                        to={`/companies/${company.id}`}
                        className="flex items-center gap-2 hover:text-signal"
                      >
                        <CompanySpine
                          hue={company.color_hue}
                          label={company.name}
                          className="h-4"
                        />
                        <CompanyMonogram name={company.name} hue={company.color_hue} />
                        <span className="truncate text-xs text-text-1">{company.name}</span>
                      </Link>
                    </td>
                    <td className="px-2">
                      <UserAvatar user={company.primary_tech_owner} withName />
                    </td>
                    <td className="num px-2 text-right text-xs text-text-2">
                      {company.active_projects}
                    </td>
                    <td className="num px-2 text-right text-xs text-text-2">
                      {company.open_tasks}
                    </td>
                    <td className="num px-2 text-right text-xs">
                      {company.overdue_tasks > 0 ? (
                        <span className="font-medium text-overdue">
                          <span aria-hidden="true">! </span>
                          {company.overdue_tasks}
                        </span>
                      ) : (
                        <span className="text-text-3">{company.overdue_tasks}</span>
                      )}
                    </td>
                    <td className="num px-2 text-right text-xs text-text-2">
                      {/*
                        El `sr-only` dice "3 contactos" y el número visible dice
                        "3": la misma información, una vez para cada forma de
                        leer. Antes decía "contactos desconocidos" para todas
                        las empresas, porque el dato no llegaba — un `aria` bien
                        escrito sobre un dato que no existe anuncia el hueco con
                        más precisión, no lo tapa.
                      */}
                      <span className="sr-only">
                        {t('companies.contactsCount', { count: company.contacts_count })}
                      </span>
                      <span aria-hidden="true">{company.contacts_count}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
